/**
 * Helper SEO : construit un alt text riche en mots-clés à partir d'un titre
 * d'image de galerie. Les mots-clés obligatoires (croix de pharmacie, croix
 * lumineuse, enseigne lumineuse pharmacie, relief lumineux pharmacie, Genève,
 * Vaud, Lausanne, Valais) sont injectés en rotation pour éviter le "keyword
 * stuffing" détecté par Google tout en couvrant l'ensemble du champ sémantique.
 */

// Mots-clés produit (rotation basée sur le contenu du titre + index)
const PRODUCT_KEYWORDS = [
  "croix de pharmacie",
  "croix lumineuse",
  "enseigne lumineuse pharmacie",
  "relief lumineux pharmacie",
];

// Villes / cantons desservis (rotation basée sur index)
const LOCATION_KEYWORDS = ["Genève", "Vaud", "Lausanne", "Valais"];

/** Retourne un mot-clé produit adapté au contenu du titre. */
function pickProductKeyword(title, index) {
  const t = (title || "").toLowerCase();
  if (t.includes("caducée") || t.includes("caduceus") || t.includes("caduceo")) {
    return "relief lumineux pharmacie";
  }
  if (t.includes("lettre") || t.includes("boîtier") || t.includes("buchstaben")) {
    return "enseigne lumineuse pharmacie";
  }
  if (t.includes("néon") || t.includes("neon")) {
    return "croix lumineuse";
  }
  if (t.includes("croix") || t.includes("kreuz") || t.includes("cross") || t.includes("croce")) {
    return "croix de pharmacie";
  }
  return PRODUCT_KEYWORDS[index % PRODUCT_KEYWORDS.length];
}

// Liste étendue des cantons/villes suisses à détecter dans le titre pour
// bypasser l'injection de ville par rotation quand une localisation est
// déjà mentionnée.
const SWISS_CITIES_KEYWORDS = [
  "genève", "geneva", "genf", "ginevra",
  "vaud", "lausanne",
  "valais", "wallis", "sion",
  "neuchâtel", "neuchatel", "neuenburg",
  "fribourg", "freiburg", "friburgo",
  "jura",
  "onex", "carouge", "cologny", "plainpalais", "grenus", "chancy",
];

/** Retourne une localisation adaptée au titre ou par rotation. */
function pickLocation(title, index) {
  const t = (title || "").toLowerCase();
  if (t.includes("genève") || t.includes("geneva") || t.includes("genf") || t.includes("ginevra")) {
    return "Genève";
  }
  if (t.includes("lausanne")) return "Lausanne";
  if (t.includes("vaud")) return "Vaud";
  if (t.includes("valais") || t.includes("wallis")) return "Valais";
  if (t.includes("neuchâtel") || t.includes("neuchatel") || t.includes("neuenburg")) return "Neuchâtel";
  if (t.includes("fribourg") || t.includes("freiburg") || t.includes("friburgo")) return "Fribourg";
  if (t.includes("jura")) return "Jura";
  return LOCATION_KEYWORDS[index % LOCATION_KEYWORDS.length];
}

/**
 * Génère un alt text SEO riche pour une image de galerie.
 * Si le titre contient déjà le mot-clé produit et/ou la ville, on ne
 * duplique pas — juste on ajoute "Signapharma Suisse" à la fin comme
 * signature de marque, sauf si "Suisse" est déjà présent.
 */
export function galleryAlt(title, index = 0) {
  const productKw = pickProductKeyword(title, index);
  const location = pickLocation(title, index);
  const tLower = (title || "").toLowerCase();

  const cityAlreadyPresent =
    tLower.includes(location.toLowerCase()) ||
    (location === "Genève" && (tLower.includes("geneva") || tLower.includes("genf") || tLower.includes("ginevra"))) ||
    (location === "Valais" && tLower.includes("wallis")) ||
    (location === "Neuchâtel" && (tLower.includes("neuchatel") || tLower.includes("neuenburg"))) ||
    (location === "Fribourg" && (tLower.includes("freiburg") || tLower.includes("friburgo"))) ||
    // Bypass si une autre localisation suisse est déjà présente dans le titre
    SWISS_CITIES_KEYWORDS.some((kw) => tLower.includes(kw));
  const productAlreadyPresent = tLower.includes(productKw.toLowerCase());
  const suisseAlreadyPresent = tLower.includes("suisse") || tLower.includes("schweiz") || tLower.includes("switzerland") || tLower.includes("svizzera");

  const parts = [title];
  if (!productAlreadyPresent) parts.push(productKw);
  if (!cityAlreadyPresent) parts.push(location);
  if (!suisseAlreadyPresent) parts.push("Signapharma Suisse");
  else parts.push("Signapharma");
  return parts.join(" — ");
}

/**
 * Construit un objet JSON-LD ImageGallery pour Schema.org (Google Rich Results).
 * Injecté une seule fois dans le <head> via <script type="application/ld+json">.
 */
export function buildGalleryJsonLd(galleryItems, lang = "fr") {
  const images = galleryItems.map((g, i) => {
    const title = g.title[lang] || g.title.fr;
    return {
      "@type": "ImageObject",
      contentUrl: g.url,
      name: title,
      description: galleryAlt(title, i),
      keywords: [
        pickProductKeyword(title, i),
        pickLocation(title, i),
        "Signapharma",
        "Suisse",
      ].join(", "),
    };
  });
  return {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    name: "Signapharma — Galerie des réalisations",
    description:
      "Galerie de croix de pharmacie, croix lumineuses, enseignes lumineuses pharmacie et reliefs lumineux fabriqués par Signapharma pour les officines de Genève, Vaud, Lausanne, Valais et toute la Suisse romande.",
    image: images,
  };
}
